import React, { useEffect, useRef, useState } from "react";
import { api, type AppNotification } from "@/api";
import { useUI } from "@/lib/theme";
import { fmtDateTime } from "@/lib/format";

function BellIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function timeAgo(iso: string, lang: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  if (lang.startsWith("es")) {
    if (mins < 1) return "ahora";
    if (mins < 60) return `hace ${mins}m`;
    if (hrs < 24) return `hace ${hrs}h`;
    return fmtDateTime(iso, "es-ES");
  } else {
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    return fmtDateTime(iso, "en-US");
  }
}

interface Props {
  demo?: boolean;
}

export function NotificationBell({ demo = false }: Props) {
  const { t, lang } = useUI();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Polling de no leídas cada 60s (solo en modo real).
  useEffect(() => {
    if (demo) return;
    const fetch = () => {
      api.notificationsUnread().then((r) => setUnread(r.unread)).catch(() => {});
    };
    void fetch();
    const id = setInterval(() => { if (!document.hidden) void fetch(); }, 60_000);
    return () => clearInterval(id);
  }, [demo]);

  // Cerrar al hacer click fuera.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = async () => {
    if (demo) return;
    setOpen((v) => !v);
    if (!open) {
      setLoading(true);
      try {
        const res = await api.notifications();
        setItems(res.notifications);
        if (unread > 0) {
          await api.markNotificationsRead();
          setUnread(0);
        }
      } catch { /* silencioso */ } finally {
        setLoading(false);
      }
    }
  };

  const badgeLabel = unread > 9 ? "9+" : String(unread);

  if (demo) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={handleOpen}
        aria-label={t("notif_title")}
        className="relative flex h-8 w-8 items-center justify-center rounded-lg text-muted transition hover:bg-line hover:text-fg"
      >
        <BellIcon className="h-5 w-5" />
        {unread > 0 && (
          <span
            aria-label={badgeLabel}
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-0.5 text-[10px] font-bold text-white"
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 z-50 w-80 rounded-2xl border border-line bg-panel shadow-xl">
          <div className="border-b border-line px-4 py-3">
            <h2 className="text-sm font-semibold text-fg">{t("notif_title")}</h2>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-accent/30 border-t-accent" role="status" />
              </div>
            )}

            {!loading && items.length === 0 && (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <BellIcon className="h-8 w-8 text-muted opacity-40" />
                <p className="text-sm text-muted">{t("notif_empty")}</p>
              </div>
            )}

            {!loading && items.map((n) => {
              let metaParams: Record<string, string | number> | undefined;
              try { metaParams = n.meta ? (JSON.parse(n.meta) as Record<string, string | number>) : undefined; } catch { /* ignorar */ }
              const title = t(n.title, metaParams) !== n.title ? t(n.title, metaParams) : n.title;
              return (
                <div
                  key={n.id}
                  className={`border-b border-line px-4 py-3 last:border-0 ${n.read_at ? "opacity-60" : ""}`}
                >
                  <p className="text-sm font-medium text-fg">{title}</p>
                  {n.body && <p className="mt-0.5 text-xs text-muted">{n.body}</p>}
                  <p className="mt-1 text-[11px] text-muted">{timeAgo(n.created_at, lang)}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
